import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const LandingPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [utmParams, setUtmParams] = useState({
    utm_source: 'N/A',
    utm_medium: 'N/A',
    utm_campaign: 'N/A',
    utm_term: 'N/A',
  });

  // Capture UTM parameters from URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {
      utm_source: urlParams.get('utm_source') || 'N/A',
      utm_medium: urlParams.get('utm_medium') || 'N/A',
      utm_campaign: urlParams.get('utm_campaign') || 'N/A',
      utm_term: urlParams.get('utm_term') || 'N/A',
    };
    setUtmParams(params);
  }, []);

  // Fix mobile viewport height issue with browser UI
  React.useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVH();
    window.addEventListener('resize', setVH);
    
    return () => window.removeEventListener('resize', setVH);
  }, []);

  const goals = [
    { id: 'weight-loss', label: 'Mršavljenje', iconUrl: 'https://imagedelivery.net/bsJiXVjZz9CYOTIw_HoJ7g/66678540-949f-468a-067c-d9f7f899cb00/public' },
    { id: 'gain-weight', label: 'Izgradnja mišića', iconUrl: 'https://imagedelivery.net/bsJiXVjZz9CYOTIw_HoJ7g/2de44788-fa0e-40eb-a4a7-28bc8f4d7a00/public' },
    { id: 'other', label: 'Ostalo', iconUrl: 'https://imagedelivery.net/bsJiXVjZz9CYOTIw_HoJ7g/c2e70f66-8667-41af-e4c8-09ced6b81500/public' },
  ];

  const toggleGoal = (goalId) => {
    setSelectedGoals((prev) =>
      prev.includes(goalId)
        ? prev.filter((id) => id !== goalId)
        : [...prev, goalId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!firstName.trim()) {
      toast.error('Molimo unesite vaše ime');
      return;
    }

    if (!lastName.trim()) {
      toast.error('Molimo unesite vaše prezime');
      return;
    }

    if (!email) {
      toast.error('Molimo unesite vaš email');
      return;
    }

    if (selectedGoals.length === 0) {
      toast.error('Molimo izaberite bar jedan cilj');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Molimo unesite validnu email adresu');
      return;
    }

    // Push to Data Layer BEFORE any API calls
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'lead_submit',
        form_name: 'Balsa – Recepti'
      });
    }

    // Prepare data for webhook
    setIsSubmitting(true);
    
    // Get current date and time
    const now = new Date();
    const date = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`;
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    // Format goals as comma-separated string
    const goalLabels = selectedGoals.map(goalId => {
      const goal = goals.find(g => g.id === goalId);
      return goal ? goal.label : '';
    }).filter(Boolean).join(', ');

    const webhookData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      goal: goalLabels,
      date: date,
      time: time,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_term: utmParams.utm_term,
    };

    try {
      // Send to Make.com webhook
      await axios.post('https://hook.eu2.make.com/7dqbr4hasie7tapt4i7fpcnlprql2lyo', webhookData);
      
      // Push submittedForm event to Data Layer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'submittedForm'
      });
      
      // Show success message and update UI
      toast.success('Hvala vam! Javićemo se uskoro.');
      setIsSubmitted(true);
    } catch (error) {
      console.error('Webhook error:', error);
      toast.error('Došlo je do greške. Molimo pokušajte ponovo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-container">
      {/* Mobile Image - Shows first on mobile */}
      <div className="landing-image mobile-only">
        <img
          src="https://imagedelivery.net/bsJiXVjZz9CYOTIw_HoJ7g/66d21fc9-dfcc-4bdd-1151-b0072a2a7d00/public"
          alt="Fitness"
          className="landing-img-mobile"
        />
      </div>

      {/* Left Section - Form Content */}
      <div className="landing-left">
        <div className="landing-content">
          {!isSubmitted ? (
            <>
              <h1 className="landing-title">
                Zdrava ishrana može biti jednostavna!
              </h1>
              <p className="landing-subtitle">
                Popunite formu i stiže vam kolekcija od 10 provjerenih, jednostavnih recepata.
              </p>

              <form id="leadForm" onSubmit={handleSubmit} className="landing-form">
            <div className="form-row">
              <Input
                type="text"
                placeholder="Ime"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="landing-input"
                required
              />
              <Input
                type="text"
                placeholder="Prezime"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="landing-input"
                required
              />
            </div>
            
            <div className="form-group">
              <Input
                type="email"
                placeholder="Unesite vašu email adresu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="landing-input"
                required
              />
            </div>

            <div className="goals-section">
              <p className="goals-label">Koji je vaš cilj?</p>
              <div className="goals-container">
              {goals.map((goal) => {
                const isSelected = selectedGoals.includes(goal.id);
                return (
                  <button
                    key={goal.id}
                    type="button"
                    onClick={() => toggleGoal(goal.id)}
                    className={`goal-card ${isSelected ? 'selected' : ''}`}
                  >
                    <img src={goal.iconUrl} alt={goal.label} className="goal-icon-img" />
                    <span className="goal-label">{goal.label}</span>
                  </button>
                );
              })}
            </div>
            </div>

                <Button type="submit" className="landing-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Šaljem...' : 'Preuzmite recepte'}
                </Button>
              </form>
            </>
          ) : (
            <div className="thank-you-container">
              <h1 className="landing-title">Hvala vam!</h1>
              <p className="landing-subtitle">
                Molimo provjerite vaš inbox (provjerite i Spam) za moj email.
              </p>
              <CheckCircle className="thank-you-icon" size={120} />
            </div>
          )}
        </div>
      </div>

      {/* Right Section - Image (Desktop only) */}
      <div className="landing-right desktop-only">
        <img
          src="https://imagedelivery.net/bsJiXVjZz9CYOTIw_HoJ7g/d70b7c22-03da-4b9a-ef96-f73e658f2200/public"
          alt="Fitness"
          className="landing-img"
        />
      </div>
    </div>
  );
};

export default LandingPage;